import { useEffect, useRef } from "react";
import type { WorldEnvironment } from "./types";

export function WorldOrb({ environment }: { environment: WorldEnvironment }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    let frame = 0;
    let animation = 0;
    const points = Array.from({ length: 900 }, (_, index) => {
      const phi = Math.acos(1 - 2 * (index + 0.5) / 900);
      const theta = Math.PI * (1 + Math.sqrt(5)) * index;
      return [Math.sin(phi) * Math.cos(theta), Math.cos(phi), Math.sin(phi) * Math.sin(theta)] as const;
    });
    const draw = () => {
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const bounds = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.floor(bounds.width * ratio));
      const height = Math.max(1, Math.floor(bounds.height * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      context.clearRect(0, 0, width, height);
      const time = frame * 0.004;
      const radius = Math.min(width, height) * 0.27;
      const red = 60 + environment.fire * 14;
      const green = 90 + environment.earth * 10;
      const blue = 110 + environment.water * 14 + environment.void * 4;
      for (const [baseX, baseY, baseZ] of points) {
        const wave = 1 + Math.sin(baseX * environment.air + baseY * environment.sky + time * 2) * 0.05;
        const x = baseX * Math.cos(time) - baseZ * Math.sin(time);
        const z = baseX * Math.sin(time) + baseZ * Math.cos(time);
        const scale = (1.5 + z * 0.45) * wave;
        const screenX = width / 2 + x * radius * scale;
        const screenY = height / 2 + baseY * radius * scale;
        const alpha = Math.max(0.08, Math.min(0.8, 0.18 + (z + 1) * 0.22 + environment.light * 0.02));
        context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${alpha})`;
        context.fillRect(screenX, screenY, ratio, ratio);
      }
      frame += 1;
      animation = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animation);
  }, [environment]);
  return <canvas ref={canvasRef} className="world-orb" aria-hidden="true" />;
}
